import { CreateSync, GetSync } from "data/libs/sync";

import { SetAlert } from "components/Alerts/actions";

import { SetInProgress } from "../actions";
import { SetClientSync } from "../clientCard/actions";

export function ResetSyncProgress() {
  return (dispatch) => {
    dispatch(SetInProgress(false, 0));
  };
}

function ResetClientSync(sync) {
  return (dispatch) => {
    dispatch(SetClientSync(sync));
  };
}

const FireError = (text) => {
  return (dispatch) => {
    dispatch(SetAlert({ type: "error", text: text }));
  };
};

const message = (e, defaultMsg) => {
  return e?.response?.data?.error || defaultMsg;
};

const calcPercentage = (current, total) => {
  return total ? (current / total) * 100 : 10;
};

const isTimeoutReached = (startDate, timeoutInMins) => {
  return (Date.now() - Date.parse(startDate)) / 1000 / 60 > timeoutInMins;
};

function CheckSyncStatus(
  syncId,
  onComplete = () => {},
  reset = ResetSyncProgress,
  update = SetInProgress
) {
  return async (dispatch) => {
    try {
      const { status, ...sync } = await GetSync(syncId);

      if (isTimeoutReached(sync.startTime, 15)) {
        dispatch(reset());
        dispatch(
          FireError(
            "CRM Sync is taking longer than expected, Please contact support for assistant"
          )
        );
        return;
      }

      switch (status) {
        case "failed":
          dispatch(reset());
          dispatch(FireError(sync.result));
          break;
        case "completed":
          onComplete();
          dispatch(update(false, 100));
          break;
        default: {
          const progress = calcPercentage(sync.currentPage, sync.totalPages);
          dispatch(update(true, progress));
          setTimeout(() => {
            dispatch(CheckSyncStatus(syncId, onComplete, reset, update));
          }, 1000);
        }
      }
    } catch (e) {
      dispatch(reset());
      dispatch(
        FireError(
          message(
            e,
            "Cannot determine CRM Sync progress, Please contact support for assistant"
          )
        )
      );
    }
  };
}

export function TriggerFullSync() {
  return async (dispatch, getState) => {
    const { advisor } = getState().hubly.data;
    const { hub } = getState().hubly.data.hub.selected;

    if (hub.clientPool.type === "hubly") {
      return;
    }

    dispatch(SetInProgress(true, 0));
    try {
      const { id } = await CreateSync({
        type: "contacts",
        advisor_id: advisor.id,
        hub_id: hub.id,
      });

      dispatch(CheckSyncStatus(id));
    } catch (e) {
      dispatch(ResetSyncProgress());
      dispatch(
        FireError(
          message(e, "Failed to initiate a CRM Sync, You may try again")
        )
      );
    }
  };
}

export function TriggerAsyncJob(steps, onComplete) {
  return async (dispatch, getState) => {
    const { advisor } = getState().hubly.data;
    const { hub } = getState().hubly.data.hub.selected;

    if (advisor.role.title !== "Administrator") return;

    dispatch(SetInProgress(true, 0));
    try {
      const { id } = await CreateSync({
        type: "async_job",
        advisor_id: advisor.id,
        hub_id: hub.id,
        steps: steps,
      });

      dispatch(CheckSyncStatus(id, onComplete));
    } catch (e) {
      dispatch(ResetSyncProgress());
      dispatch(
        FireError(message(e, "Failed to initiate async job. You may try again"))
      );
    }
  };
}

export function TriggerMeetingsSync() {
  return async (dispatch, getState) => {
    const { advisor } = getState().hubly.data;
    const { hub } = getState().hubly.data.hub.selected;

    if (hub.clientPool.type === "hubly") {
      return;
    }

    dispatch(SetInProgress(true, 0));
    try {
      const { id } = await CreateSync({
        type: "meetings",
        advisor_id: advisor.id,
        hub_id: hub.id,
      });

      dispatch(CheckSyncStatus(id));
    } catch (e) {
      dispatch(ResetSyncProgress());
      dispatch(
        FireError(
          message(e, "Failed to initiate a Meetings Sync, You may try again")
        )
      );
    }
  };
}

export function TriggerClientMeetingsSync(clientId, onComplete, onError) {
  return async (dispatch, getState) => {
    const { advisor } = getState().hubly.data;
    const { hub } = getState().hubly.data.hub.selected;
    const { activeClients } = getState().hubly.data.hub.clients;

    const shouldSyncMeetings =
      advisor.integrations.find((i) => {
        return i.hubId === hub.id;
      }) &&
      hub?.preferences?.sync?.shouldSyncMeetings &&
      !activeClients[clientId].isDeletedInCrm;

    if (!shouldSyncMeetings) {
      return;
    }

    dispatch(SetClientSync({ meetings: { inProgress: true, percent: 0 } }));

    try {
      const { id } = await CreateSync({
        type: "contact_meetings",
        advisor_id: advisor.id,
        client_id: clientId,
        hub_id: hub.id,
      });

      dispatch(
        CheckSyncStatus(
          id,
          onComplete,
          () => {
            return dispatch(
              ResetClientSync({ meetings: { inProgress: false, percent: 0 } })
            );
          },
          (inProgress, percent) => {
            return dispatch(
              SetClientSync({
                meetings: { inProgress: inProgress, percent: percent },
              })
            );
          }
        )
      );
    } catch (e) {
      dispatch(
        ResetClientSync({ meetings: { inProgress: false, percent: 0 } })
      );
      onError(message(e, "Failed to initiate a CRM Sync, You may try again"));
    }
  };
}

export default TriggerFullSync;
